/**
 * 웹진 응답 타입.
 *
 * 백엔드 `ModelWebzine::toIssue()` 와 1:1 이다. 예전에는 같은 모양을
 * `src/data/webzine.ts` 가 상수로 들고 있었지만 이제 DB(`MC_WEBZINE`)에서 온다.
 *
 * 편집은 관리 화면(`/admin/webzine.html`)에서 한다.
 */

/** 스트리밍 서비스 링크. 아이콘은 `public/images/stream/<id>.svg` */
export interface StreamingService {
  /**
   * CSS 모디파이어이자 아이콘 파일명.
   * 서버가 `MC_CAMPAIGN_URL.F_LINK_TYPE` 을 변환해 준다
   * (S=spotify, Y=youtube-music, M=melon, B=bugs, G=genie, F=flo, V=vibe).
   */
  id: string;
  label: string;
  href: string;
}

export interface WebzineIssue {
  /** URL 조각 — `/webzine/<slug>` */
  slug: string;
  vol: string;
  /** 곡 제목. 커버에서 줄을 나눠야 하면 `\n` */
  title: string;
  artist: string;
  cover: string;
  /**
   * QR 이 가리킬 주소. **그림이 아니라 링크**다.
   * 관리 화면에서 비워 두면 서버가 그 호의 공개 주소로 채워 준다.
   */
  qr: string;
  /** 발행일 YYYYMMDD */
  date: string;

  /** 목록(`/webzine`)과 다른 호의 'Back Issues' 에 쓰는 카드 정보 */
  card: {
    thumbnail: string;
    /** 커버 제목과 달리 편집된 한 줄 제목이다 */
    title: string;
    description: string;
  };

  story: {
    /** `\n` 으로 줄을 나눈다 */
    heading: string;
    /** 문단 배열 — 서버가 빈 줄 기준으로 나눠 준다 */
    body: string[];
  };

  /** 인물·전문이 모두 채워진 호에만 있다 */
  interview: {
    photo: string;
    name: string;
    intro: string;
    /** 한 칸이 한 줄 */
    summary: string[];
    full: {
      heading: string;
      /** 문단 배열 */
      body: string[];
    };
  } | null;

  /**
   * 미션. **연결된 캠페인이 정본**이다.
   *
   * 캠페인이 연결되지 않은 호는 `null` 이고, 화면은 미션 영역을 통째로 감춘다.
   */
  mission: {
    /** 캠페인의 첫 번째 노출 이미지. 없으면 웹진 커버 */
    thumbnail: string;
    /** 실제 지급액 — 서버가 `F_REWARD × F_REWARD_MULTIPLE` 로 계산한다 */
    reward: number;
    /** MC_CAMPAIGN.F_SEQ — `/play/:id` */
    campaignSeq: number;
    /** 캠페인의 노출 제목 */
    title: string;
    /**
     * 미션 형식 — `M` 음악 / `V` 영상. `MC_CAMPAIGN.F_TYPE`.
     * 화면이 이 값으로 모양을 고른다(앨범 카드 / 유튜브 썸네일).
     */
    type: string | null;
  } | null;

  /** 연결된 캠페인의 링크에서 온다. 캠페인이 없으면 빈 배열 */
  streaming: StreamingService[];

  eventBanner: {
    image: string;
    href: string | null;
  } | null;
}

/** 발행일 `YYYYMMDD` → `2026. 06. 10` (목록 카드 표기) */
export function formatIssueDate(value: string | undefined): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length !== 8) return '';
  return `${digits.slice(0, 4)}. ${digits.slice(4, 6)}. ${digits.slice(6, 8)}`;
}

/** 커버 제목처럼 `\n` 이 들어간 값을 alt/타이틀용 한 줄로 편다. */
export function flattenTitle(title: string): string {
  return String(title ?? '').replace(/\n/g, ' ');
}

/**
 * 호의 정식 주소.
 *
 * 최신 호는 홈이 곧 그 호라서 `/` 로 보낸다 (시안 목록도 최신 호를
 * `index.html` 로 건다).
 */
export function issueHref(issue: WebzineIssue, latestSlug: string | null): string {
  return issue.slug === latestSlug ? '/' : `/webzine/${issue.slug}`;
}
